import type { TFunction } from 'i18next';
import { CANONICAL_PHASE_KEYS, PHASE_KEY_TO_I18N_KEY, PhaseKey } from './phase-labels.type';
import type { PhaseLabel, ResolvedPhaseLabels } from './phase-labels.type';

/**
 * Resolves the canonical-keyed display labels for the seven workflow phases
 * by merging the backend-supplied `phases` companion array on top of the
 * existing `common.workflow_steps.*` i18n defaults.
 *
 * Responsibilities:
 * 1. Iterate `CANONICAL_PHASE_KEYS` so the returned map always has every key
 *    in canonical order — chart legends, tile rows, and tooltip itemSorters
 *    can index without fallback branches.
 * 2. For each canonical key, prefer a trimmed non-empty `phases[].label`
 *    over the i18n default. Empty strings, whitespace-only labels, non-string
 *    values, and unknown keys are silently dropped (so a stale BE row never
 *    causes an empty label to render).
 * 3. Fall back to `t('common.workflow_steps.<alias>')` when no usable
 *    override is provided. The alias mapping lives in
 *    `PHASE_KEY_TO_I18N_KEY` and reconciles the historical naming drift
 *    between backend keys and dictionary identifiers.
 *
 * @param {PhaseLabel[] | undefined} phases - Optional `phases` array from a
 *   phase-aware backend response. `undefined` is treated as "no overrides".
 * @param {TFunction} t - Translation function from `useTranslation()`. Calls
 *   `t('common.workflow_steps.<alias>')` once per canonical key that lacks
 *   a usable override.
 * @returns {ResolvedPhaseLabels} Record keyed by `PhaseKey` with the resolved
 *   display label for every canonical phase. Never contains empty strings.
 */
function resolvePhaseLabels(phases: PhaseLabel[] | undefined, t: TFunction): ResolvedPhaseLabels {
  const overrideByKey = new Map<string, string>();
  if (Array.isArray(phases)) {
    for (const entry of phases) {
      if (!entry || typeof entry !== 'object') {
        continue;
      }
      const key = entry.key;
      const label = entry.label;
      if (typeof key !== 'string' || typeof label !== 'string') {
        continue;
      }
      const trimmed = label.trim();
      if (!trimmed) {
        continue;
      }
      overrideByKey.set(key, trimmed);
    }
  }

  const resolved = {} as ResolvedPhaseLabels;
  for (const phaseKey of CANONICAL_PHASE_KEYS) {
    const override = overrideByKey.get(phaseKey);
    resolved[phaseKey] = override ?? t(PHASE_KEY_TO_I18N_KEY[phaseKey]);
  }
  return resolved;
}

/**
 * Indexes a `ResolvedPhaseLabels` map by an arbitrary string key, returning
 * the canonical-key label when the input matches a known `PhaseKey` and
 * `undefined` otherwise. Useful for chart components that operate on
 * dynamic key strings (e.g. `extractPhaseKeys` output) and want a
 * compile-safe lookup without casting.
 *
 * @param {ResolvedPhaseLabels} labels - Resolver output to index into.
 * @param {string} key - Candidate phase key (untrusted).
 * @returns {string | undefined} The resolved label, or `undefined` if the
 *   key is not one of the seven canonical phases.
 */
function getResolvedLabel(labels: ResolvedPhaseLabels, key: string): string | undefined {
  if (Object.values(PhaseKey).includes(key as PhaseKey)) {
    return labels[key as PhaseKey];
  }
  return undefined;
}

export { getResolvedLabel, resolvePhaseLabels };
